import { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '@/utils/auth';
import { apiClient } from '@/services/apiClient';
import { normalizeConnectionsPayload } from '@/lib/normalizeConnectionsPayload';

export interface ConnectionDoctor {
  id: string;
  name: string;
  email: string;
  profileId?: string;
  specialty?: string;
  crm?: string;
  crmState?: string;
}

export interface Connection {
  id: string;
  patientId: string;
  doctorId: string;
  firstEncounterDate: string;
  lastEncounterDate: string;
  source: 'on-demand' | 'scheduled';
  status: 'active' | 'revoked';
  lastAppointmentId?: string | null;
  patient?: {
    id: string;
    name: string;
    email: string;
  };
  doctor?: ConnectionDoctor | null;
}

export function useConnections(role: 'PATIENT' | 'DOCTOR') {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConnections = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const endpoint =
        role === 'DOCTOR'
          ? '/medical-records/my-patients'
          : '/medical-records/my-doctors';

      const token = getAuthToken();
      if (!token) {
        setConnections([]);
        setIsLoading(false);
        return;
      }

      const result = await apiClient.get<Connection[]>(endpoint);

      if (result.success) {
        setConnections(normalizeConnectionsPayload<Connection>(result.data));
      } else {
        const isNoVincule =
          result.error?.toLowerCase().includes('nenhum') ||
          result.error?.toLowerCase().includes('não possui');
        if (isNoVincule) {
          setConnections([]);
        } else {
          throw new Error(result.error || 'Erro desconhecido');
        }
      }
    } catch (err) {
      console.error('Error in useConnections:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar conexões');
      setConnections([]);
    } finally {
      setIsLoading(false);
    }
  }, [role]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  return {
    connections,
    isLoading,
    error,
    refresh: fetchConnections,
  };
}

export function usePatientHistory(patientId: string | null) {
  const [history, setHistory] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = async () => {
    if (!patientId) return;

    try {
      setIsLoading(true);
      const token = getAuthToken();
      if (!token) return;

      const result = await apiClient.get<any[]>(
        `/medical-records/patient/${patientId}/history`,
      );

      if (result.success) {
        setHistory(result.data || []);
      } else {
        throw new Error(result.error || 'Erro desconhecido');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar histórico');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) {
      fetchHistory();
    }
  }, [patientId]);

  return {
    history,
    isLoading,
    error,
    refresh: fetchHistory,
  };
}
